import React, {
  useCallback,
  useEffect,
  useState,
  useContext,
  useMemo,
} from 'react';
import clsx from 'clsx';
import {
  useFormContext,
} from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Popper from '@mui/material/Popper';
import isNil from 'lodash/isNil';
import orderBy from 'lodash/orderBy';
import Controller from 'core/components/FormController';
import Button from 'pages/Dashboard/components/Button';
import DateRangePicker from 'core/components/DateTimePicker/DateRangePicker';
import MediaPreview from 'pages/Dashboard/components/MediaPreview';
import Spinner from 'pages/Dashboard/components/Spinner';
import TextAreaWithActions from 'pages/Dashboard/components/TextAreaWithActions';
import {
  ServiceAllocation,
} from 'pages/Dashboard/pages/Billing/types';
import {
  ClaimAdjustment,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/EOBAdjustmentCodePopover';
import {
  allocationDefaultKeys,
  getClaimIdsWithCPRCMissingServiceAllocation,
  populateEOBAllocation,
  syncWithAdjustments,
} from 'pages/Dashboard/pages/Billing/Payments/utils';
import {
  billingEventChannel,
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  WizardContext,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/wizardContext';
import EOBPaymentInfo from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/EOBPaymentInfo';
import ToolTip from 'core/components/Tooltip';
import Table from 'core/components/VirtualTable';
import {
  eobRelatedReadonlyColumns,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/columns';
import PatientName from 'views/EMR/components/PatientName';
import SelectableProvider from 'core/context/selectable';
import ClaimVisitSummary from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/ClaimVisitSummary';
import delay from 'lodash/delay';
import {
  useLocation,
} from 'react-router-dom';
import useDeleteMediaHandler from 'pages/Dashboard/hooks/useDeleteMediaHandler';
import {
  MultimediaType,
  MediaOriginatorType,
} from 'pages/Dashboard/utils/constants';
import PatientInsuranceSelect from 'pages/Dashboard/components/PatientInsuranceSelect';
import {
  InsuranceDTO,
} from 'dtos/insuranceDTO';
import {
  ClaimPaymentRemarkCodeParams,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/ClaimPaymentRemarkCodesPopover';
import {
  HealthCareRemarkCodeDTO,
} from 'dtos/healthCareRemarkCodeDTO';
import omit from 'lodash/omit';
import {
  EOBClaimPaymentInfoDTO,
} from 'dtos';

export function AllocationStepTitle() {
  const { control } = useFormContext();
  const { currentPatient, onClose } = useContext(WizardContext);
  const location = useLocation();
  const pathName = useMemo(() => new RegExp(location.pathname.split('/')?.[1]), []);

  return (
    <div className="text-sm text-gray-500 font-semibold flex items-center gap-1 whitespace-nowrap min-w-0 max-w-[48rem] overflow-visible">
      Claims for
      <PatientName
        patient={currentPatient}
        onNavigateAway={onClose}
        className="text-gray-500"
        currentPath={pathName}
      />
      <span>at</span>
      <Controller
        control={control}
        name="payer"
        rules={{ required: true }}
        render={({ field: { value, onChange, ...field } }) => (
          <PatientInsuranceSelect
            {...field}
            patientId={currentPatient?.patientId ?? 0}
            classes={{
              select: '!py-[3px] rounded',
              value: '!max-w-fit',
            }}
            insuranceName={value?.payerName}
            placeholder="Select Patient Insurance"
            className="pl-1 min-w-0"
            editable={false}
            hasDefault={false}
            onChange={(option) => {
              const { insuranceName, payerEntry, ...insurance } = option as InsuranceDTO;
              onChange({
                payerName: insuranceName,
                payerId: payerEntry?.payerId,
                ...insurance,
              });
            }}
          />
        )}
      />
    </div>
  );
}

export default function AllocationStep() {
  const {
    visibleDoc,
    addPatient,
    dateRange,
    setDateRange,
    currentPatient,
    fetchingBillingData,
    setInReview,
    billingData,
    toggleDoc,
    onClose,
    currentEOB,
    hasEOBServiceCodePaymentInfos,
    handleEOBDrop,
    removeEOBDocument,
    adjustmentExceptionMap,
  } = useContext(WizardContext);
  const hasData = billingData.length > 0;
  const { watch, control, setValue, getValues } = useFormContext();
  const [
    eobNumber,
    eobDoc,
    eobServiceCodePaymentsInfo,
    eobClaimPayments,
  ] = watch([
    'eob.eobNumber',
    'eobDoc',
    'eob.eobServiceCodePaymentInfos',
    'eob.eobClaimPayments',
  ]);
  const [popperProps, setPopperProps] = useState<[
    HTMLElement | null,
    ServiceAllocation[]]>([null, []]);
  const [popperElement, relatedAllocation] = popperProps;
  const open = !isNil(popperElement);
  const invalidCprcClaimIds = useMemo(
    () => getClaimIdsWithCPRCMissingServiceAllocation(
      eobServiceCodePaymentsInfo ?? [],
      eobClaimPayments ?? [],
    ),
    [eobServiceCodePaymentsInfo, eobClaimPayments],
  );
  const canProceedToReview = hasEOBServiceCodePaymentInfos && invalidCprcClaimIds.length === 0;
  let reviewDisabledReason = '';
  if (!hasEOBServiceCodePaymentInfos) {
    reviewDisabledReason = 'No Allocation for claim service codes';
  } else if (invalidCprcClaimIds.length > 0) {
    reviewDisabledReason = 'CPRC added for a claim without service allocation';
  }

  useEffect(() => {
    let scrollContainer: HTMLElement | null = null;
    const checkVisibility = () => {
      if (!document?.contains(popperElement)) {
        setPopperProps([null, []]);
      }
    };

    if (!isNil(popperElement)) {
      scrollContainer = popperElement.closest('.populate-virtual-list-scroll');
      scrollContainer?.addEventListener('scroll', checkVisibility);
    }

    return () => {
      scrollContainer?.removeEventListener('scroll', checkVisibility);
    };
  }, [popperElement]);

  const { onMediaDelete } = useDeleteMediaHandler();
  const eobData = orderBy(
    populateEOBAllocation(
      eobNumber,
      currentPatient ?? {},
      billingData,
      eobServiceCodePaymentsInfo,
      eobClaimPayments,
      adjustmentExceptionMap,
    ),
    [
      (option) => option?.eobNumbers?.has(currentEOB?.eob?.eobNumber ?? ''),
      'hasEOBs',
      'visitDate',
      'allocation.length',
      'encounterId',
    ],
    ['desc', 'asc', 'desc', 'desc', 'asc'],
  );

  const updateAllocation = useCallback((
    rowIndex: number,
    id: string,
    value: number | string | ClaimAdjustment[] | HealthCareRemarkCodeDTO[],
    original: ServiceAllocation,
  ) => {
    const info = getValues('eob.eobServiceCodePaymentInfos') ?? [];

    const _index = info.findIndex((item: ServiceAllocation) => (
      item.claim?.claimId === original.claim?.claimId
      && item.id === original.id
    ));
    const index = _index === -1 ? info.length : _index;
    const updatedValue = isNil(value) || typeof value === 'object' || id === 'comment'
      ? value
      : Number(value);

    setValue('eob.eobServiceCodePaymentInfos', [
      ...info.slice(0, index),
      syncWithAdjustments({
        ...original,
        ...(id === 'adjustments' ? {
          deductible: null,
          coinsurance: null,
          copay: null,
        } : {}),
        ...({
          ...(original.adjustmentBaseAmounts ?? {}),
          [id]: original.adjustmentBaseAmounts ?? 0,
        }),
        [id]: updatedValue,
      }, id, adjustmentExceptionMap),
      ...info.slice(index + 1),
    ], { shouldDirty: true });
  }, []);

  const handleClear = (claimId: number, id?: number) => {
    const values = (getValues('eob.eobServiceCodePaymentInfos') ?? [])
      .map((item: ServiceAllocation) => omit(
        item,
        (item?.claim?.claimId === claimId && (!isNil(id) ? item?.id === id : true))
          ? allocationDefaultKeys
          : [],
      ));
    setValue('eob.eobServiceCodePaymentInfos', values, { shouldDirty: true });
  };

  const handleExclude = (claimId: number, id?: number, isExcluded?: boolean) => {
    const values = (getValues('eob.eobServiceCodePaymentInfos') ?? [])
      .map((item: ServiceAllocation) => (
        item?.claim?.claimId === claimId && (!isNil(id) ? item?.id === id : true)
          ? { ...item, isExcluded }
          : item));
    setValue('eob.eobServiceCodePaymentInfos', values, { shouldDirty: true });
  };

  const handleClaimPaymentRemarkCodes = ({
    claimId,
    eobClaimPayments,
  }: ClaimPaymentRemarkCodeParams) => {
    const currentClaimPayments = (getValues('eob.eobClaimPayments') ?? []) as EOBClaimPaymentInfoDTO[];
    const remainingClaimPayments = currentClaimPayments.filter((item) => item?.claimId !== claimId);
    setValue(
      'eob.eobClaimPayments',
      [...remainingClaimPayments, ...(eobClaimPayments ?? [])],
      { shouldDirty: true },
    );
  };

  useEffect(() => {
    const unsubscribeFromAdjustments = paymentsEventChannel.on(
      'onChangeEOBAdjustmentCode',
      ({ index, adjustments, data }) => {
        updateAllocation(index, 'adjustments', adjustments, data);
      },
    );

    const unsubscribeFromRemarkCodes = paymentsEventChannel.on(
      'onChangeEOBRemarkCodes',
      ({ index, healthCareRemarkCodes, data }) => {
        updateAllocation(index, 'healthCareRemarkCodes', healthCareRemarkCodes, data);
      },
    );

    const unsubscribeFromServiceCodeComment = paymentsEventChannel.on(
      'onChangeEOBServiceCodeComment',
      ({ index, comment, data }) => {
        updateAllocation(index, 'comment', comment, data);
      },
    );
    const unsubscribeFromClaimPaymentRemarkCodes = paymentsEventChannel.on(
      'onChangeEOBClaimPaymentRemarkCodes',
      handleClaimPaymentRemarkCodes,
    );

    const unsubscribeFromAllocationExclude = paymentsEventChannel.on('onEOBAllocationExclude', handleExclude);

    const unsubscribeFromAllocation = paymentsEventChannel.on('onEOBAllocationDelete', handleClear);
    return () => {
      unsubscribeFromAdjustments();
      unsubscribeFromAllocation();
      unsubscribeFromRemarkCodes();
      unsubscribeFromServiceCodeComment();
      unsubscribeFromClaimPaymentRemarkCodes();
      unsubscribeFromAllocationExclude();
    };
  }, []);

  useEffect(() => {
    const unsubscribe = billingEventChannel.on('onRelatedAllocation', (props) => {
      delay(() => {
        setPopperProps(popperElement?.isSameNode?.(props[0])
          ? [null, []] : props);
      }, 350);
    });

    return () => {
      unsubscribe();
    };
  }, [popperElement]);

  return (
    <>
      <div className="flex flex-1 flex-row gap-3 !max-h-full overflow-y-hidden">
        <div className={clsx('flex flex-col justify-between gap-2', {
          'w-3/5': visibleDoc,
          'w-full': !visibleDoc,
        })}
        >
          <EOBPaymentInfo />
          <div className="flex-1 flex flex-col justify-start gap-2 overflow-y-hidden">
            <DateRangePicker
              value={dateRange}
              onChange={(nextDateRange) => {
                if (nextDateRange) {
                  setDateRange(nextDateRange);
                }
              }}
            />
            <div className={clsx('flex flex-col w-full', {
              'overflow-y-auto gap-3': hasData,
              'overflow-y-hidden h-[calc(100%-20px)] items-center justify-center': fetchingBillingData || !hasData,
            })}
            >
              <Popper
                className="z-[1300] bg-white border-x border-x-gray-200 rounded overflow-hidden ml-8"
                open={open}
                placement="top"
                anchorEl={popperElement}
                style={{
                  width: popperElement?.clientWidth,
                }}
              >
                <div className="w-full h-full flex flex-col">
                  <div className="flex items-center justify-between bg-primary text-white">
                    <h3 className="font-semibold text-xs pl-2">Related EOB Data</h3>
                    <IconButton
                      onClick={() => { setPopperProps([null, []]); }}
                    >
                      <CloseIcon className="!w-5 !h-5 !text-white" />
                    </IconButton>
                  </div>
                  <div className="flex w-full max-h-[240px] overflow-y-hidden">
                    <Table<ServiceAllocation>
                      columns={eobRelatedReadonlyColumns}
                      data={relatedAllocation}
                      className="populate-breakdown-table"
                      classes={{
                        root: '!h-auto',
                        body: '!m-0 !p-0',
                        scrollContainer: '!p-0',
                        header: 'px-4 !items-end w-full',
                        outerColumn: '!items-end',
                        column: '!text-left',
                        outerRow: '!w-full',
                        windowsClassName: '!w-full',
                        scrollbarGutter: 'inherit',
                        row: '!items-stretch',
                        cell: '!h-auto !py-0',
                      }}
                    />
                  </div>
                </div>
              </Popper>
              <SelectableProvider>
                {eobData?.length > 0 && (
                  <ClaimVisitSummary
                    data={eobData}
                    update={updateAllocation}
                    fetchingBillingData={fetchingBillingData}
                  />
                )}
              </SelectableProvider>
              {fetchingBillingData && (
                <div className="flex items-center gap-2">
                  <Spinner size={30} />
                  Loading...
                </div>
              )}
              {(!fetchingBillingData && !hasData) && <p>No Encounters Available.</p>}
            </div>
          </div>
          <Controller
            control={control}
            name="eob.comment"
            render={({ field }) => (
              <TextAreaWithActions
                {...field}
                placeholder="Add Comment"
                textAreaClassName="pb-8"
                rows={3}
                id="eob-comment"
                trackingLabel="EOB Comment"
              />
            )}
          />
        </div>
        {visibleDoc && (
          <div className="flex w-2/5">
            <MediaPreview
              media={eobDoc?.media!}
              className="!overflow-auto !max-w-full !max-h-[100vh-64px]"
              thumbnail
              zoomable
              onDelete={() => {
                if (!isNil(eobDoc?.media?.mediaId)) {
                  onMediaDelete({
                    list: [
                      {
                        originatorType: MediaOriginatorType.Billing,
                        mediaId: eobDoc.media.mediaId ?? 0,
                        fileName: eobDoc.media.fileName ?? '',
                      },
                    ],
                  });
                } else {
                  removeEOBDocument();
                }
              }}
              onUpdate={handleEOBDrop}
              mediaType={MultimediaType.EOB}
            />
          </div>
        )}
      </div>
      <div className="flex justify-between items-center gap-3">
        <Button
          variant="outlined"
          type="button"
          onClick={onClose}
        >
          Close
        </Button>
        <div className="flex flex-row justify-end gap-3">
          <Button
            variant="outlined"
            type="button"
            onClick={() => {
              addPatient(null);
            }}
          >
            Back
          </Button>
          <Button
            variant="outlined"
            type="button"
            onClick={() => {
              addPatient(null);
            }}
          >
            Add Another Patient
          </Button>
          <ToolTip
            title={reviewDisabledReason}
            hide={canProceedToReview}
          >
            <div className={clsx(
              'w-full',
              { 'cursor-not-allowed': !canProceedToReview },
            )}
            >
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={!canProceedToReview}
                onClick={() => {
                  setInReview(true);
                  toggleDoc(false);
                }}
              >
                Review
              </Button>
            </div>
          </ToolTip>
        </div>
      </div>
    </>
  );
}
